<template>
  <section
    id="experience"
    class="section experience"
    aria-labelledby="experience-title"
  >
    <div class="container">
      <header class="section-heading">
        <p class="section-heading__index mono">03 / Experience</p>
        <div class="section-heading__copy">
          <h2 id="experience-title">Built in the real world.</h2>
          <p>
            Product development, consulting, technical leadership, and
            solution architecture across enterprise systems.
          </p>
        </div>
      </header>

      <div class="experience__layout">
        <aside class="experience__intro">
          <p class="mono">Career path</p>
          <h3>From implementation to technical direction.</h3>
          <p>
            Roles across cloud products, identity, e-commerce, public
            transport, consulting, and architecture.
          </p>
        </aside>

        <ol class="experience__timeline">
          <li v-for="item in timelineItems" :key="item.date + item.title">
            <time class="experience__date mono">{{ item.date }}</time>
            <span class="experience__dot" aria-hidden="true"></span>
            <article class="experience__item">
              <h3>{{ item.title }}</h3>
              <p>{{ item.description }}</p>
              <ul
                v-if="item.technologies.length"
                class="experience__tech"
                :aria-label="`Technologies used as ${item.title}`"
              >
                <li v-for="technology in item.technologies" :key="technology">
                  {{ technology }}
                </li>
              </ul>
            </article>
          </li>
        </ol>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "Experience",
  data() {
    return {
      timelineItems: [
        {
          date: "Feb 2022",
          title: "Developer at Softhouse",
          description:
            "Consultant at Softhouse, serving as a solution architect at Skånetrafiken in Malmö.",
          technologies: [
            "C#",
            "WPF",
            "Azure",
            "Kubernetes",
            "DevOps",
            "Angular",
          ],
        },
        {
          date: "Jun 2020",
          title: "Developer at Telenor",
          description: "Worked on telenor.se.",
          technologies: [
            "C#",
            "Episerver",
            "ASP.NET",
            "Vue",
            "Docker",
            "AWS",
            "Elastic Beanstalk",
            "CloudWatch",
          ],
        },
        {
          date: "Oct 2018",
          title: "CTO and consultant at Digital Spark",
          description:
            "Co-founded the consultancy. Led technical delivery and knowledge development while consulting at Telenor.",
          technologies: [],
        },
        {
          date: "Nov 2016",
          title: "Consultant at XLENT",
          description:
            "Resource consultant for Assembly, Kunskapsskolan, Interflora, Tunstall, and Telenor.",
          technologies: [
            ".NET",
            "Java",
            "Node.js",
            "Episerver",
            "Golang",
            "Vue",
            "AngularJS",
            "SQL",
          ],
        },
        {
          date: "Apr 2016",
          title: "Consultant at Axians",
          description:
            "Built web applications and APIs for IAM projects using Forefront Identity Manager, now Microsoft Identity Manager. Synchronized identity data across customer systems and kept it consistent.",
          technologies: ["FIM", "MIM", "Web applications", "APIs"],
        },
        {
          date: "Oct 2015",
          title: "Developer at Cloudfinder",
          description:
            "My first developer job, learning from senior colleagues. Built a cloud file search and versioning product spanning Box, Office 365, Salesforce, and Google.",
          technologies: [
            "Java",
            "C#",
            "Docker",
            "Elasticsearch",
            "MongoDB",
          ],
        },
        {
          date: "Jul 2014",
          title: "Trainee at CGI",
          description:
            "Summer trainee while in school. Modernized internal salary interfaces using DevExpress components.",
          technologies: ["DevExpress"],
        },
      ],
    };
  },
};
</script>

<style lang="scss" scoped>
.experience__layout {
  display: grid;
  grid-template-columns: minmax(250px, 0.68fr) minmax(0, 1.32fr);
  gap: clamp(42px, 8vw, 104px);
  padding: clamp(34px, 5vw, 58px);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  background: var(--surface);
  box-shadow: var(--shadow-soft);
}

.experience__intro {
  align-self: start;
  position: sticky;
  top: 32px;
  padding-top: 4px;
}

.experience__intro > p:first-child {
  margin: 0 0 var(--space-4);
  color: var(--accent);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.11em;
  text-transform: uppercase;
}

.experience__intro h3 {
  max-width: 12ch;
  font-size: clamp(27px, 3vw, 38px);
  font-weight: 650;
  letter-spacing: -0.045em;
  line-height: 1.15;
}

.experience__intro > p:last-child {
  max-width: 34ch;
  margin: var(--space-5) 0 0;
  color: var(--muted);
  font-size: 13px;
  line-height: 1.7;
}

.experience__timeline {
  position: relative;
  margin: 0;
  padding: 0;
  list-style: none;
}

.experience__timeline::before {
  content: "";
  position: absolute;
  top: 10px;
  bottom: 24px;
  left: 95px;
  width: 1px;
  background: linear-gradient(var(--accent), var(--border) 16%, var(--border) 86%, transparent);
}

.experience__timeline > li {
  position: relative;
  display: grid;
  grid-template-columns: 76px 18px minmax(0, 1fr);
  gap: 10px;
  padding-bottom: 18px;
}

.experience__date {
  padding-top: 23px;
  color: var(--accent-2);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-align: right;
  text-transform: uppercase;
}

.experience__dot {
  position: relative;
  z-index: 1;
  width: 9px;
  height: 9px;
  margin-top: 25px;
  margin-left: 5px;
  border: 2px solid var(--surface-solid);
  border-radius: 50%;
  background: var(--accent);
  box-shadow: 0 0 0 3px rgba(124, 226, 201, 0.12);
}

.experience__item {
  padding: 20px 22px;
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  background: rgba(7, 12, 17, 0.46);
  transition: border-color 180ms ease, transform 180ms ease;
}

.experience__item:hover {
  border-color: rgba(124, 226, 201, 0.3);
  transform: translateX(3px);
}

.experience__item h3 {
  color: var(--text);
  font-size: 16px;
  font-weight: 680;
  letter-spacing: -0.02em;
}

.experience__item > p {
  margin: 9px 0 0;
  color: var(--muted);
  font-size: 13px;
  line-height: 1.68;
}

.experience__tech {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 15px 0 0;
  padding: 0;
  list-style: none;
}

.experience__tech li {
  padding: 4px 7px;
  border: 1px solid var(--border);
  border-radius: 5px;
  color: var(--text-soft);
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 8px;
  letter-spacing: 0.03em;
}

@media (max-width: 820px) {
  .experience__layout {
    grid-template-columns: 1fr;
    gap: 42px;
  }

  .experience__intro {
    position: static;
  }

  .experience__intro h3 {
    max-width: 18ch;
  }
}

@media (max-width: 560px) {
  .experience__layout {
    padding: 32px 20px;
    border-radius: var(--radius-lg);
  }

  .experience__timeline::before {
    left: 6px;
  }

  .experience__timeline > li {
    grid-template-columns: 14px minmax(0, 1fr);
    gap: 10px;
    padding-bottom: 14px;
  }

  .experience__date {
    grid-column: 2;
    padding: 0 0 5px 1px;
    text-align: left;
  }

  .experience__dot {
    grid-row: 1 / span 2;
    grid-column: 1;
    margin-top: 5px;
    margin-left: 2px;
  }

  .experience__item {
    grid-column: 2;
    padding: 18px;
  }
}
</style>
